import {
  Box,
  Button,
  Checkbox,
  Chip,
  Group,
  NativeSelect,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import type { Format, FormatRules } from '@quizee/core';
import { questionTypeLabel } from '../format';
import {
  MAX_OPTIONS,
  MIN_OPTIONS,
  isChoice,
  newOptionDraft,
  withQuestionType,
  type OptionDraft,
  type OutcomeDraft,
  type QuestionDraft,
} from '../interaction/draft';

/**
 * One question, whatever the format.
 *
 * Everything that differs between a poll question and a trivia question is asked of FORMAT_RULES
 * rather than of the format name: whether the type can be changed, whether an answer can be marked
 * correct, whether it points at an outcome. A new format that fits the rules table gets a working
 * editor here without this file being touched.
 */
export function QuestionEditor({
  question,
  index,
  format,
  rules,
  outcomes,
  onChange,
}: {
  question: QuestionDraft;
  index: number;
  format: Format;
  rules: FormatRules;
  outcomes: readonly OutcomeDraft[];
  onChange: (question: QuestionDraft) => void;
}) {
  const choice = isChoice(question.type);

  function setOption(key: string, patch: Partial<OptionDraft>) {
    onChange({
      ...question,
      options: question.options.map((option) =>
        option.key === key ? { ...option, ...patch } : option,
      ),
    });
  }

  function markOnlyCorrect(key: string) {
    onChange({
      ...question,
      options: question.options.map((option) => ({ ...option, isCorrect: option.key === key })),
    });
  }

  const rows = question.options.map((option, optionIndex) => (
    <OptionRow
      key={option.key}
      option={option}
      index={optionIndex}
      question={question}
      rules={rules}
      outcomes={outcomes}
      onLabel={(label) => setOption(option.key, { label })}
      onCorrect={(isCorrect) => setOption(option.key, { isCorrect })}
      onWeights={(weights) => setOption(option.key, { weights })}
      onRemove={() =>
        onChange({
          ...question,
          options: question.options.filter((candidate) => candidate.key !== option.key),
        })
      }
    />
  ));

  const correctKey = question.options.find((option) => option.isCorrect)?.key ?? '';

  return (
    <Stack gap="sm">
      {rules.questionTypes.length > 1 && (
        <NativeSelect
          id={`question-type-${question.key}`}
          label="Type"
          description="How readers answer this one."
          value={question.type}
          onChange={(event) =>
            onChange(
              withQuestionType(question, event.target.value as QuestionDraft['type'], format),
            )
          }
          data={rules.questionTypes.map((type) => ({
            value: type,
            label: questionTypeLabel(type),
          }))}
          style={{ maxWidth: '22rem' }}
        />
      )}

      <TextInput
        id={`question-prompt-${question.key}`}
        label="Question"
        description="What readers are being asked."
        value={question.prompt}
        maxLength={1000}
        onChange={(event) => onChange({ ...question, prompt: event.target.value })}
      />

      {question.type === 'free_text' && (
        <TextInput
          id={`question-placeholder-${question.key}`}
          label="Placeholder"
          description="Optional. The faint text in the box before a reader types."
          value={question.placeholder}
          maxLength={200}
          onChange={(event) => onChange({ ...question, placeholder: event.target.value })}
          style={{ maxWidth: '26rem' }}
        />
      )}

      {question.type === 'free_text' && (
        <Radio.Group
          label="How much room does the answer need?"
          description={
            question.multiline === null
              ? 'Not chosen yet, so the embed decides.'
              : 'One line for a name or a postcode, a paragraph for something a reader wants to tell you.'
          }
          value={roomValue(question.multiline)}
          onChange={(value) => onChange({ ...question, multiline: value === 'paragraph' })}
        >
          <Group gap="lg" mt={6}>
            <Radio value="one_line" label="One line" />
            <Radio value="paragraph" label="A paragraph" />
          </Group>
        </Radio.Group>
      )}

      {question.type === 'scale' && <ScaleFields question={question} onChange={onChange} />}

      {choice && (
        <div>
          <Text component="span" fw={500} size="sm" display="block">
            Options
          </Text>
          <Text component="span" c="dimmed" size="xs" display="block" mb="xs">
            Between {MIN_OPTIONS} and {MAX_OPTIONS} answers. {optionsHint(rules, question.type)}
          </Text>

          {rules.requiresCorrectAnswers && question.type === 'single_choice' ? (
            <Radio.Group
              value={correctKey}
              onChange={markOnlyCorrect}
              aria-label="Which answer is correct"
            >
              <Stack gap="xs">{rows}</Stack>
            </Radio.Group>
          ) : (
            <Stack gap="xs">{rows}</Stack>
          )}

          <Button
            variant="default"
            size="compact-sm"
            mt="sm"
            onClick={() =>
              onChange({ ...question, options: [...question.options, newOptionDraft()] })
            }
            disabled={question.options.length >= MAX_OPTIONS}
            aria-label={addOptionLabel(rules, index)}
          >
            Add option
          </Button>
        </div>
      )}
    </Stack>
  );
}

/**
 * The "add option" button's name.
 *
 * A format with one question needs no more than the words on the button. A quiz with twelve
 * questions has twelve of these buttons, and a screen reader reading "Add option" twelve times
 * over says nothing about which question it would add to.
 */
function addOptionLabel(rules: FormatRules, index: number): string | undefined {
  return rules.maxQuestions === 1 ? undefined : `Add option to question ${String(index + 1)}`;
}

/**
 * Which of the two is ticked, and neither when nobody has said.
 *
 * An empty value is the honest reading of an absent flag: the question has not been given a
 * shape, and the widget is still deciding from the format. Ticking "one line" on its behalf
 * would put words in the publisher's mouth.
 */
function roomValue(multiline: boolean | null): string {
  if (multiline === null) return '';
  return multiline ? 'paragraph' : 'one_line';
}

function optionsHint(rules: FormatRules, type: QuestionDraft['type']): string {
  if (rules.requiresCorrectAnswers) {
    return type === 'single_choice'
      ? 'Tick the answer that is right.'
      : 'Tick every answer that is right.';
  }
  if (rules.requiresOutcomes) return 'Point each answer at the outcome it suggests.';
  return type === 'single_choice' ? 'Readers pick one.' : 'Readers can pick more than one.';
}

function ScaleFields({
  question,
  onChange,
}: {
  question: QuestionDraft;
  onChange: (question: QuestionDraft) => void;
}) {
  function setScale(patch: Partial<QuestionDraft['scale']>) {
    onChange({ ...question, scale: { ...question.scale, ...patch } });
  }

  return (
    <Group align="flex-start" gap="md" wrap="wrap">
      <TextInput
        id={`scale-min-${question.key}`}
        label="Lowest number"
        inputMode="numeric"
        value={question.scale.min}
        maxLength={4}
        onChange={(event) => setScale({ min: event.target.value })}
        w={140}
      />
      <TextInput
        id={`scale-max-${question.key}`}
        label="Highest number"
        inputMode="numeric"
        value={question.scale.max}
        maxLength={4}
        onChange={(event) => setScale({ max: event.target.value })}
        w={140}
      />
      <TextInput
        id={`scale-min-label-${question.key}`}
        label="Label for the lowest"
        description="Optional."
        placeholder="Not at all"
        value={question.scale.minLabel}
        maxLength={60}
        onChange={(event) => setScale({ minLabel: event.target.value })}
        w={200}
      />
      <TextInput
        id={`scale-max-label-${question.key}`}
        label="Label for the highest"
        description="Optional."
        placeholder="Very much"
        value={question.scale.maxLabel}
        maxLength={60}
        onChange={(event) => setScale({ maxLabel: event.target.value })}
        w={200}
      />
    </Group>
  );
}

function OptionRow({
  option,
  index,
  question,
  rules,
  outcomes,
  onLabel,
  onCorrect,
  onWeights,
  onRemove,
}: {
  option: OptionDraft;
  index: number;
  question: QuestionDraft;
  rules: FormatRules;
  outcomes: readonly OutcomeDraft[];
  onLabel: (label: string) => void;
  onCorrect: (isCorrect: boolean) => void;
  onWeights: (weights: Record<string, number>) => void;
  onRemove: () => void;
}) {
  const number = index + 1;
  const correct = rules.requiresCorrectAnswers && option.isCorrect;

  return (
    <Box
      p={6}
      data-correct={correct ? 'true' : undefined}
      style={{
        borderRadius: 'var(--mantine-radius-md)',
        // A tinted row is what makes the right answer readable at a glance in a long quiz. The
        // tick alone disappears once there are forty of them on the page.
        background: correct ? 'var(--mantine-color-sage-light)' : 'transparent',
      }}
    >
      <Group gap="xs" wrap="nowrap" align="center">
        {rules.requiresCorrectAnswers &&
          (question.type === 'single_choice' ? (
            <Radio
              value={option.key}
              color="sage"
              aria-label={`Option ${String(number)} is the correct answer`}
            />
          ) : (
            <Checkbox
              checked={option.isCorrect}
              color="sage"
              onChange={(event) => onCorrect(event.currentTarget.checked)}
              aria-label={`Option ${String(number)} is a correct answer`}
            />
          ))}
        <TextInput
          id={`option-${option.key}`}
          aria-label={`Option ${String(number)}`}
          value={option.label}
          maxLength={300}
          placeholder={`Option ${String(number)}`}
          onChange={(event) => onLabel(event.target.value)}
          style={{ flex: 1 }}
        />
        <Button
          variant="subtle"
          color="gray"
          size="compact-sm"
          onClick={onRemove}
          disabled={question.options.length <= MIN_OPTIONS}
          aria-label={`Remove option ${String(number)}`}
        >
          Remove
        </Button>
      </Group>

      {rules.requiresOutcomes && (
        <OutcomePointer option={option} number={number} outcomes={outcomes} onWeights={onWeights} />
      )}
    </Box>
  );
}

/**
 * What an answer means in a personality quiz.
 *
 * The content model stores a number per outcome, and the obvious editor for that is a grid of
 * numeric inputs: options down one side, outcomes across the top. Nobody writing a quiz thinks
 * that way. They think "this answer is a Traditionalist answer", so that is the control: one chip
 * per outcome, on or off, and a plain sentence underneath saying where the answer points.
 *
 * Turning a chip on is a weight of one, which is all a hand written quiz ever needs. A weight that
 * arrived some other way (an import, or a model draft) is kept exactly as it is while its chip
 * stays on, and called out underneath, so nothing is silently flattened by an editor who only
 * came here to fix a typo.
 */
function OutcomePointer({
  option,
  number,
  outcomes,
  onWeights,
}: {
  option: OptionDraft;
  number: number;
  outcomes: readonly OutcomeDraft[];
  onWeights: (weights: Record<string, number>) => void;
}) {
  function toggle(outcomeKey: string, on: boolean) {
    const weights = { ...option.weights };
    if (on) weights[outcomeKey] = weights[outcomeKey] ?? 1;
    else delete weights[outcomeKey];
    onWeights(weights);
  }

  const named = outcomes.map((outcome, index) => ({
    key: outcome.key,
    name: outcome.title.trim() === '' ? `Outcome ${String(index + 1)}` : outcome.title.trim(),
  }));
  const pointsAt = named.filter((outcome) => outcome.key in option.weights);
  const heavier = pointsAt.filter((outcome) => (option.weights[outcome.key] ?? 1) !== 1);

  return (
    <Box role="group" aria-label={`Option ${String(number)} points at`} pl={4} mt={6} pb={4}>
      <Group gap={6} align="center" wrap="wrap">
        <Text component="span" size="xs" c="dimmed">
          Points at
        </Text>
        {named.map((outcome) => (
          <Chip
            key={outcome.key}
            size="xs"
            radius="sm"
            variant="light"
            checked={outcome.key in option.weights}
            onChange={(checked) => toggle(outcome.key, checked)}
          >
            {outcome.name}
          </Chip>
        ))}
      </Group>
      <Text size="xs" c="dimmed" mt={4}>
        {pointsAt.length === 0
          ? 'Not pointing anywhere yet. Every answer has to point at an outcome.'
          : `Choosing this makes a reader more of ${listNames(pointsAt.map((outcome) => outcome.name))}.`}
        {heavier.length > 0 &&
          ` ${heavier
            .map(
              (outcome) =>
                `${outcome.name} counts ${String(option.weights[outcome.key] ?? 1)} times.`,
            )
            .join(' ')}`}
      </Text>
    </Box>
  );
}

function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${String(names[names.length - 1])}`;
}
